import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  Button,
  Pagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { fetchEventSummaries } from '../../controllers/paymentsController';
import { getCurrencyDisplayLabel } from '../../utils/currencyDisplay';

// Backend enum mappingleri
const EventPaymentStatusNames = {
  0: 'Beklemede',
  1: 'Ödendi',
  2: 'İptal Edildi',
  3: 'Onay Bekliyor',
};

const EventDues = () => {
  const navigate = useNavigate();

  // Event summaries listesi ve pagination
  const [eventSummaries, setEventSummaries] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filtreler
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Yardımcı state'ler
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Event summaries yükle
  const loadEventSummaries = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        pageNumber: page,
        pageSize,
        startDate: startDate || null,
        endDate: endDate || null,
        status: statusFilter !== '' ? Number(statusFilter) : null,
        searchTerm: searchTerm?.trim() || null,
      };

      const { data, meta } = await fetchEventSummaries(params);
      setEventSummaries(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      setError(e?.message || 'Etkinlik özetleri alınamadı');
      setEventSummaries([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEventSummaries();
  }, [page, statusFilter, startDate, endDate]);

  const handleSearchSubmit = () => {
    setPage(1);
    loadEventSummaries();
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  const handleEventClick = (eventId) => {
    navigate(`/payments/event-dues/${eventId}`);
  };

  if (loading && eventSummaries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>Etkinlik Ödemeleri</Typography>
      </Stack>

      {/* Filtreler */}
      <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Durum
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(EventPaymentStatusNames).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ minWidth: 170 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Başlangıç Tarihi
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>

            <Box sx={{ minWidth: 170 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Bitiş Tarihi
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>

            <Box sx={{ minWidth: 250 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Genel Arama (etkinlik adı)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit();
                  }
                }}
                placeholder="Etkinlik adı ile ara..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>

            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearchSubmit}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  backgroundColor: '#0E4D94',
                  boxShadow: 'none',
                  py: 1.2,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#0d3d7a',
                    boxShadow: 'none',
                  },
                }}
              >
                Uygula
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Event Summaries Listesi */}
      {eventSummaries.length === 0 ? (
        <Alert severity="info">Filtrelere uygun etkinlik bulunamadı.</Alert>
      ) : (
        <Stack spacing={2}>
          {eventSummaries.map((summary) => (
            <Paper
              key={summary.eventId}
              elevation={0}
              sx={{
                border: '1px solid #e7e7e7',
                boxShadow: 'none',
                borderRadius: '8px',
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#0E4D94',
                  boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                },
              }}
              onClick={() => handleEventClick(summary.eventId)}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <EventIcon sx={{ fontSize: 32, color: '#0E4D94' }} />
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444' }}>
                      {summary.eventTitle || 'Bilinmeyen Etkinlik'}
                    </Typography>
                    <ArrowForwardIcon sx={{ fontSize: 18, color: '#a3a3a3' }} />
                  </Stack>
                  <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                    {summary.eventStartDate && (
                      <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                        {formatDate(summary.eventStartDate)}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                      Toplam Ödeme: {summary.totalPayments}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                      Toplam Tutar: {summary.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencyDisplayLabel('TRY')}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                      Toplam Katılımcı: {summary.totalParticipantCount}
                    </Typography>
                    {summary.paidCount > 0 && (
                      <Chip
                        label={`${summary.paidCount} Ödendi`}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: '#DFFFEA',
                          color: '#17C653',
                        }}
                      />
                    )}
                    {summary.pendingCount > 0 && (
                      <Chip
                        label={`${summary.pendingCount} Beklemede`}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: '#E7EDF4',
                          color: '#0E4D94',
                        }}
                      />
                    )}
                    {summary.awaitingApprovalCount > 0 && (
                      <Chip
                        label={`${summary.awaitingApprovalCount} Onay Bekliyor`}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: '#FFF4E6',
                          color: '#FF9800',
                        }}
                      />
                    )}
                    {summary.cancelledCount > 0 && (
                      <Chip
                        label={`${summary.cancelledCount} İptal`}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: '#F5F5F5',
                          color: '#666666',
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size="small" />
        </Box>
      )}
    </Box>
  );
};

export default EventDues;
